import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { parseKizenContractingRequestRecord, type KizenPipelineRecord } from "@aam/shared";
import { getSessionToken } from "../hooks/useSession";
import { Button } from "./ui/Button";
import { Skeleton } from "./ui/Skeleton";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface PinnacleSyncRun {
  id: string;
  triggerType: string;
  requestedBy: string | null;
  status: string;
  fetchedCount: number;
  upsertedCount: number;
  fetchedContractCount: number;
  upsertedContractCount: number;
  unchangedAgentCount: number;
  unchangedContractCount: number;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
  artifactCount?: number;
}

interface PinnacleSyncArtifact {
  fileName: string;
  size: number;
  createdAt: string;
  updatedAt: string;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function artifactKey(runId: string, fileName: string) {
  return `${runId}/${fileName}`;
}

async function fetchRunArtifactImage(runId: string, fileName: string, token: string) {
  const response = await fetch(
    `${API_BASE}/api/pinnacle/sync/runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(fileName)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok) {
    throw new Error(`Failed to load ${fileName}`);
  }

  return URL.createObjectURL(await response.blob());
}

interface PinnacleAgentRecord {
  id: string;
  externalId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  uplineExternalId: string | null;
  uplineName: string | null;
  uplineNpn: string | null;
  matchedUserId: string | null;
  matchedBy: string | null;
  matchedAt: string | null;
  rawPayload: Record<string, unknown> | null;
  lastSyncedAt: string;
}

interface PinnacleContractRecord {
  id: string;
  externalAgentId: string;
  contractExternalId: string;
  carrierName: string | null;
  writingNo: string | null;
  statusName: string | null;
  uplineExternalId: string | null;
  uplineName: string | null;
  uplineNpn: string | null;
  uplineWritingId: string | null;
  contractDate: string | null;
  termDate: string | null;
  rawPayload: Record<string, unknown> | null;
  lastSyncedAt: string;
  agent?: {
    externalId: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    rawPayload: Record<string, unknown> | null;
  } | null;
}

interface KizenContractRow {
  requestName: string;
  typeOfRequest: string;
  carrier: string;
  stage: string;
  requestStatus: string;
  missingInformation: string;
  requestingAgentAgency: string;
  directUpline: string;
  agentLevel: string;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asString(value: unknown): string | undefined {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
}

function parseKizenContractRow(contract: PinnacleContractRecord): KizenContractRow {
  const record = asRecord(contract.rawPayload) as unknown as KizenPipelineRecord;
  const row = parseKizenContractingRequestRecord(record);
  const agentPayload = asRecord(contract.agent?.rawPayload);
  const agentDisplayName = asString(agentPayload.displayName);
  const agentNumber = asString(agentPayload.agentNumber);

  let requestingAgentAgency = row.requestingAgentAgency.label;
  if (!requestingAgentAgency && agentDisplayName) {
    requestingAgentAgency = agentNumber ? `${agentDisplayName} - ${agentNumber}` : agentDisplayName;
  }

  return {
    requestName: row.requestName || "—",
    typeOfRequest: row.typeOfRequest || "—",
    carrier: contract.carrierName || row.carrier || "—",
    stage: row.stage || "—",
    requestStatus: contract.statusName || row.contractingRequestStatus || "—",
    missingInformation:
      row.missingInformation.length > 0 ? row.missingInformation.join(", ") : "—",
    requestingAgentAgency: requestingAgentAgency || "—",
    directUpline:
      contract.uplineName ||
      row.directUpline.label ||
      contract.uplineNpn ||
      row.directUpline.npn ||
      "—",
    agentLevel: row.agentLevel || "—",
  };
}

function agentDisplayLabel(agent: PinnacleAgentRecord) {
  const payload = asRecord(agent.rawPayload);
  const displayName = asString(payload.displayName);
  const agentNumber = asString(payload.agentNumber);
  if (displayName && agentNumber) return `${displayName} - ${agentNumber}`;
  if (displayName) return displayName;
  const name = [agent.firstName, agent.lastName].filter(Boolean).join(" ").trim();
  return name || agent.externalId;
}

function fmt(value: string | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

export function PinnacleData() {
  const token = useMemo(() => getSessionToken(), []);
  const [loading, setLoading] = useState(true);
  const [runningSync, setRunningSync] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runs, setRuns] = useState<PinnacleSyncRun[]>([]);
  const [agents, setAgents] = useState<PinnacleAgentRecord[]>([]);
  const [contracts, setContracts] = useState<PinnacleContractRecord[]>([]);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [runArtifacts, setRunArtifacts] = useState<Record<string, PinnacleSyncArtifact[]>>({});
  const [artifactImages, setArtifactImages] = useState<Record<string, string>>({});
  const [artifactsLoadingRunId, setArtifactsLoadingRunId] = useState<string | null>(null);

  const contractRows = useMemo(
    () => contracts.map((contract) => ({ contract, display: parseKizenContractRow(contract) })),
    [contracts]
  );

  async function loadAll() {
    if (!token) {
      setError("You need to log in before viewing Pinnacle data.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const headers = { Authorization: `Bearer ${token}` };
      const [runsRes, agentsRes, contractsRes] = await Promise.all([
        fetch(`${API_BASE}/api/pinnacle/sync/runs?limit=25`, { headers }),
        fetch(`${API_BASE}/api/pinnacle/sync/agents?limit=500`, { headers }),
        fetch(`${API_BASE}/api/pinnacle/sync/contracts?limit=1000`, { headers }),
      ]);

      if (!runsRes.ok || !agentsRes.ok || !contractsRes.ok) {
        throw new Error("Failed to load Pinnacle data.");
      }

      const [runsJson, agentsJson, contractsJson] = await Promise.all([
        runsRes.json(),
        agentsRes.json(),
        contractsRes.json(),
      ]);

      setRuns((runsJson.data ?? []) as PinnacleSyncRun[]);
      setAgents((agentsJson.data ?? []) as PinnacleAgentRecord[]);
      setContracts((contractsJson.data ?? []) as PinnacleContractRecord[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load Pinnacle data.");
    } finally {
      setLoading(false);
    }
  }

  async function loadRunArtifacts(runId: string) {
    if (!token) return;

    setArtifactsLoadingRunId(runId);

    try {
      const response = await fetch(
        `${API_BASE}/api/pinnacle/sync/runs/${encodeURIComponent(runId)}/artifacts`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw new Error("Failed to load sync run artifacts.");
      }

      const json = await response.json();
      const artifacts = (json.data?.artifacts ?? []) as PinnacleSyncArtifact[];
      setRunArtifacts((current) => ({ ...current, [runId]: artifacts }));

      const nextImages: Record<string, string> = {};
      for (const artifact of artifacts) {
        const key = artifactKey(runId, artifact.fileName);
        nextImages[key] = await fetchRunArtifactImage(runId, artifact.fileName, token);
      }
      setArtifactImages((current) => ({ ...current, ...nextImages }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load sync run artifacts.");
    } finally {
      setArtifactsLoadingRunId(null);
    }
  }

  async function toggleRunArtifacts(runId: string) {
    if (expandedRunId === runId) {
      setExpandedRunId(null);
      return;
    }

    setExpandedRunId(runId);
    if (!runArtifacts[runId]) {
      await loadRunArtifacts(runId);
    }
  }

  async function runSyncNow() {
    if (!token) return;
    try {
      setRunningSync(true);
      setError(null);
      const res = await fetch(`${API_BASE}/api/pinnacle/sync/run`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || "Failed to start Pinnacle sync job.");
      }
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to start sync.");
    } finally {
      setRunningSync(false);
    }
  }

  useEffect(() => {
    loadAll();
    const id = setInterval(loadAll, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    return () => {
      Object.values(artifactImages).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [artifactImages]);

  return (
    <div className="max-w-[1400px] mx-auto space-y-6 px-4 py-8 text-content-primary">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-accent mb-2">Pinnacle / Kizen</p>
            <h1 className="text-3xl font-bold text-content-primary">Pinnacle Data</h1>
            <p className="text-content-secondary mt-2">
              Contracting requests synced from Kizen, displayed with the same columns as the Kizen pipeline table.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={runSyncNow}
              disabled={runningSync}
              size="sm"
            >
              {runningSync ? "Starting sync..." : "Run sync now"}
            </Button>
            <Button
              onClick={loadAll}
              size="sm"
              variant="secondary"
            >
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-status-rejected-border bg-status-rejected-bg p-3 text-sm text-status-rejected-text">
            <span>{error}</span>
            <Button size="sm" variant="secondary" onClick={() => void loadAll()}>Retry</Button>
          </div>
        )}

        {loading ? (
          <div className="space-y-5" role="status" aria-busy="true" aria-label="Loading Pinnacle data">
            <Skeleton className="h-64 rounded-xl" />
            <Skeleton className="h-52 rounded-xl" />
            <Skeleton className="h-52 rounded-xl" />
          </div>
        ) : (
          <>
            <section className="rounded-xl border border-border-default bg-surface-raised p-4 shadow-card">
              <h2 className="text-lg font-semibold text-content-primary mb-1">
                Contracting Requests ({contracts.length})
              </h2>
              <p className="text-xs text-content-muted mb-3">
                Matches Kizen pipeline columns. Each row is one carrier contract request for an agent/agency.
              </p>
              <div className="overflow-auto">
                <table className="w-full text-sm min-w-[1200px]">
                  <thead className="text-content-secondary">
                    <tr>
                      <th className="text-left py-2 pr-3 whitespace-nowrap">Contracting Request Name</th>
                      <th className="text-left py-2 pr-3 whitespace-nowrap">Type Of Request</th>
                      <th className="text-left py-2 pr-3 whitespace-nowrap">Requested Carrier-Contract</th>
                      <th className="text-left py-2 pr-3 whitespace-nowrap">Stage</th>
                      <th className="text-left py-2 pr-3 whitespace-nowrap">Contracting Request Status</th>
                      <th className="text-left py-2 pr-3 whitespace-nowrap">Missing Information</th>
                      <th className="text-left py-2 pr-3 whitespace-nowrap">Requesting Agent/Agency</th>
                      <th className="text-left py-2 pr-3 whitespace-nowrap">Direct Upline</th>
                      <th className="text-left py-2 pr-3 whitespace-nowrap">Requested Agent Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contractRows.map(({ contract, display }) => (
                      <tr key={contract.id} className="border-t border-border-subtle align-top">
                        <td className="py-2 pr-3 max-w-[260px]">
                          <div className="text-content-primary">{display.requestName}</div>
                          <div className="text-[11px] text-content-muted mt-0.5 font-mono">{contract.contractExternalId.slice(0, 8)}...</div>
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap">{display.typeOfRequest}</td>
                        <td className="py-2 pr-3">{display.carrier}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">{display.stage}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">{display.requestStatus}</td>
                        <td className="py-2 pr-3">{display.missingInformation}</td>
                        <td className="py-2 pr-3">{display.requestingAgentAgency}</td>
                        <td className="py-2 pr-3">{display.directUpline}</td>
                        <td className="py-2 pr-3 max-w-[220px]">{display.agentLevel}</td>
                      </tr>
                    ))}
                    {contractRows.length === 0 && (
                      <tr>
                        <td colSpan={9} className="py-8 text-center text-content-muted">
                          No contracting requests synced yet. Run a sync to pull data from Kizen.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-xl border border-border-default bg-surface-raised p-4 shadow-card">
              <h2 className="text-lg font-semibold text-content-primary mb-1">Agents ({agents.length})</h2>
              <p className="text-xs text-content-muted mb-3">
                Grouped by Kizen requesting agent/agency. One agent can have many contracting requests above.
              </p>
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead className="text-content-secondary">
                    <tr>
                      <th className="text-left py-2 pr-3">Requesting Agent/Agency</th>
                      <th className="text-left py-2 pr-3">Contracts</th>
                      <th className="text-left py-2 pr-3">Upline</th>
                      <th className="text-left py-2 pr-3">Email</th>
                      <th className="text-left py-2 pr-3">Matched User</th>
                      <th className="text-left py-2 pr-3">Last Synced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((row) => {
                      const contractCount = contracts.filter((c) => c.externalAgentId === row.externalId).length;
                      return (
                        <tr key={row.id} className="border-t border-border-subtle">
                          <td className="py-2 pr-3">{agentDisplayLabel(row)}</td>
                          <td className="py-2 pr-3">{contractCount}</td>
                          <td className="py-2 pr-3">{row.uplineName || row.uplineNpn || "—"}</td>
                          <td className="py-2 pr-3">{row.email || "—"}</td>
                          <td className="py-2 pr-3">
                            {row.matchedUserId ? (
                              <Link to={`/dashboard/agents/${row.matchedUserId}`} className="text-accent hover:underline">
                                View agent
                              </Link>
                            ) : "—"}
                          </td>
                          <td className="py-2 pr-3">{fmt(row.lastSyncedAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-xl border border-border-default bg-surface-raised p-4 shadow-card">
              <h2 className="text-lg font-semibold text-content-primary mb-3">Sync Runs</h2>
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead className="text-content-secondary">
                    <tr>
                      <th className="text-left py-2 pr-3">Started</th>
                      <th className="text-left py-2 pr-3">Status</th>
                      <th className="text-left py-2 pr-3">Trigger</th>
                      <th className="text-left py-2 pr-3">Pending Updated</th>
                      <th className="text-left py-2 pr-3">Kizen Rows</th>
                      <th className="text-left py-2 pr-3">Stored / Unchanged</th>
                      <th className="text-left py-2 pr-3">Error</th>
                      <th className="text-left py-2 pr-3">Artifacts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((run) => {
                      const hasArtifacts = (run.artifactCount ?? 0) > 0;
                      const isExpanded = expandedRunId === run.id;
                      const artifacts = runArtifacts[run.id] ?? [];

                      return (
                        <Fragment key={run.id}>
                          <tr key={run.id} className="border-t border-border-subtle">
                            <td className="py-2 pr-3">{fmt(run.startedAt)}</td>
                            <td className="py-2 pr-3">{run.status}</td>
                            <td className="py-2 pr-3">{run.triggerType}</td>
                            <td className="py-2 pr-3">{run.upsertedCount}/{run.fetchedCount}</td>
                            <td className="py-2 pr-3">{run.fetchedContractCount}</td>
                            <td className="py-2 pr-3">
                              {run.upsertedContractCount} stored / {run.unchangedContractCount ?? 0} unchanged
                            </td>
                            <td className="py-2 pr-3 text-status-rejected-text">{run.errorMessage || "—"}</td>
                            <td className="py-2 pr-3">
                              {hasArtifacts ? (
                                <button
                                  type="button"
                                  onClick={() => toggleRunArtifacts(run.id)}
                                  className="text-accent hover:underline text-left"
                                >
                                  {isExpanded ? "Hide" : "View"} {run.artifactCount} screenshot
                                  {(run.artifactCount ?? 0) === 1 ? "" : "s"}
                                </button>
                              ) : run.status === "failed" ? (
                                <span className="text-content-muted">None</span>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr key={`${run.id}-artifacts`} className="border-t border-border-subtle">
                              <td colSpan={8} className="py-4 px-2">
                                {artifactsLoadingRunId === run.id ? (
                                  <p role="status" className="text-sm text-content-muted">Loading screenshots…</p>
                                ) : artifacts.length === 0 ? (
                                  <p className="text-sm text-content-muted">No screenshots for this run.</p>
                                ) : (
                                  <div className="grid gap-4 md:grid-cols-2">
                                    {artifacts.map((artifact) => {
                                      const key = artifactKey(run.id, artifact.fileName);
                                      return (
                                        <figure
                                          key={key}
                                          className="rounded-xl border border-border-default bg-surface-base overflow-hidden"
                                        >
                                          <div className="aspect-video bg-surface-overlay flex items-center justify-center">
                                            {artifactImages[key] ? (
                                              <img
                                                src={artifactImages[key]}
                                                alt={`${run.id} ${artifact.fileName}`}
                                                className="w-full h-full object-contain"
                                              />
                                            ) : (
                                              <span role="status" className="text-sm text-content-muted">Loading image…</span>
                                            )}
                                          </div>
                                          <figcaption className="p-3 border-t border-border-default">
                                            <p className="font-mono text-xs text-content-primary break-all">
                                              {artifact.fileName}
                                            </p>
                                            <p className="text-xs text-content-muted mt-1">
                                              {formatBytes(artifact.size)} · {new Date(artifact.updatedAt).toLocaleString()}
                                            </p>
                                          </figcaption>
                                        </figure>
                                      );
                                    })}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
    </div>
  );
}
